import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useRealtimeSubscription from '../../../hooks/useRealtimeSubscription';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#f5f3ff', '#4f46e5'];

const DATE_RANGES = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', days: 365 },
];

const RevenueAttributionPanel = ({ brandId }) => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [attributionData, setAttributionData] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [topInfluencer, setTopInfluencer] = useState(null);

  const loadAttribution = useCallback(async () => {
    if (!brandId) return;
    try {
      const since = new Date();
      since?.setDate(since?.getDate() - dateRange);

      const { data, error } = await supabase
        ?.from('transactions')
        ?.select(`
          id, amount, purchase_value, status, created_at,
          influencer:user_profiles!transactions_influencer_id_fkey(id, full_name, email)
        `)
        ?.eq('brand_id', brandId)
        ?.eq('status', 'approved')
        ?.gte('created_at', since?.toISOString());

      if (!error && data) {
        const influencerMap = {};
        let total = 0;

        data?.forEach(t => {
          const inf = t?.influencer;
          if (!inf) return;
          const revenue = parseFloat(t?.purchase_value) || parseFloat(t?.amount) || 0;
          total += revenue;
          if (!influencerMap?.[inf?.id]) {
            influencerMap[inf.id] = {
              id: inf?.id,
              name: inf?.full_name || inf?.email?.split('@')?.[0] || 'Influencer',
              revenue: 0,
              transactions: 0,
            };
          }
          influencerMap[inf.id].revenue += revenue;
          influencerMap[inf.id].transactions += 1;
        });

        const sorted = Object.values(influencerMap)?.sort((a, b) => b?.revenue - a?.revenue)?.slice(0, 8)?.map((inf, idx) => ({
            ...inf,
            percentage: total > 0 ? ((inf?.revenue / total) * 100)?.toFixed(1) : '0.0',
            fill: COLORS?.[idx % COLORS?.length],
          }));

        setAttributionData(sorted);
        setTotalRevenue(total);
        setTopInfluencer(sorted?.[0] || null);
      }
    } catch (err) {
      console.error('[RevenueAttributionPanel] error:', err);
    } finally {
      setLoading(false);
    }
  }, [brandId, dateRange]);

  useEffect(() => {
    setLoading(true);
    loadAttribution();
  }, [loadAttribution]);

  useRealtimeSubscription({
    channelName: `brand-rev-attr-${brandId}`,
    table: 'transactions',
    filter: brandId ? `brand_id=eq.${brandId}` : undefined,
    enabled: !!brandId,
    onChange: () => loadAttribution(),
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const d = payload?.[0]?.payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground text-sm">{d?.name}</p>
          <p className="text-primary text-sm">${d?.revenue?.toFixed(2)} revenue</p>
          <p className="text-muted-foreground text-xs">{d?.percentage}% of total</p>
          <p className="text-muted-foreground text-xs">{d?.transactions} transactions</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="DollarSign" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Revenue Attribution</h3>
            <p className="text-xs text-muted-foreground">Influencer contribution to total revenue</p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {DATE_RANGES?.map(r => (
            <button
              key={r?.days}
              onClick={() => setDateRange(r?.days)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                dateRange === r?.days
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              {r?.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : attributionData?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="PieChart" size={40} className="text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground text-sm">No approved transactions in this period</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="flex flex-col items-center">
            <div className="w-full" style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={attributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="revenue">
                    {attributionData?.map((entry, index) => (
                      <Cell key={entry?.id} fill={entry?.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center mt-2">
              <p className="text-2xl font-bold text-foreground">${totalRevenue?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">Total Revenue ({DATE_RANGES?.find(r => r?.days === dateRange)?.label})</p>
            </div>
          </div>

          {/* Breakdown List */}
          <div className="space-y-3">
            {attributionData?.map((inf, idx) => (
              <div key={inf?.id} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: inf?.fill }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground truncate">{inf?.name}</span>
                    <span className="text-sm font-semibold text-foreground ml-2">${inf?.revenue?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${inf?.percentage}%`, backgroundColor: inf?.fill }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right">{inf?.percentage}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {topInfluencer && (
        <div className="mt-4 pt-4 border-t border-border flex items-center gap-2">
          <Icon name="TrendingUp" size={14} className="text-emerald-500" />
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{topInfluencer?.name}</span> is your top revenue driver with {topInfluencer?.percentage}% attribution
          </p>
        </div>
      )}
    </div>
  );
};

export default RevenueAttributionPanel;
